// One session clock for all CMS pages. No background/keystroke renewal.
import Vue from 'vue'
import axios from 'axios'
import store from '../store/store'

export const sessionState = Vue.observable({ warning: false, remaining: 0, busy: false, error: '', redirecting: false })
const WARNING_MS = 5 * 60 * 1000
let started = false, callback = null, active = () => false, pending = null, offset = 0
let lastActivity = Date.now()
export function getLastActivity() { return lastActivity }
export function idleMs() { return Date.now() - lastActivity }
export function tokenExpiresAt() {
    try {
        const token = localStorage.getItem('accessToken')
        if (!token) return 0
        const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        const payload = JSON.parse(atob(part))
        return Number.isFinite(payload.exp) ? payload.exp * 1000 : 0
    } catch (_) { return 0 }
}
export function msUntilExpiry() { const exp = tokenExpiresAt(); return exp ? exp - Date.now() - offset : -1 }
export function endSession(reason = 'expired') {
    if (sessionState.redirecting || !active()) return
    sessionState.redirecting = true
    sessionState.warning = false
    // Editors can synchronously retain their local drafts before the identity is cleared.
    window.dispatchEvent(new CustomEvent('cms-session-ending'))
    store.commit('LOGOUT')
    delete axios.defaults.headers.common.token
    if (callback) callback(reason)
}
export function checkSession() {
    if (!active()) { sessionState.warning = false; sessionState.redirecting = false; return }
    if (sessionState.redirecting) return
    const token = localStorage.getItem('accessToken')
    if (!token || msUntilExpiry() <= 0) { endSession(); return }
    axios.defaults.headers.common.token = token
    sessionState.remaining = Math.max(0, Math.ceil(msUntilExpiry() / 1000))
    sessionState.warning = msUntilExpiry() <= WARNING_MS
    if (!sessionState.warning) sessionState.error = ''
}
export function refreshNow() {
    if (pending) return pending
    const original = localStorage.getItem('accessToken')
    if (!original || msUntilExpiry() <= 0) { endSession(); return Promise.reject(new Error('expired')) }
    sessionState.busy = true
    sessionState.error = ''
    pending = axios.get('/api/login/check', { timeout: 10000, headers: { token: original } })
        .then(res => {
            const token = res.data && res.data.newtoken
            if (!token) throw new Error('missing token')
            // An old response must not resurrect a logged-out/replaced session.
            if (sessionState.redirecting || localStorage.getItem('accessToken') !== original) return false
            store.commit('LOGIN', token)
            axios.defaults.headers.common.token = token
            checkSession()
            if (sessionState.warning) sessionState.error = '최대 로그인 시간이 가까워졌습니다. 남은 시간 안에 저장하고 다시 로그인해 주세요.'
            return true
        })
        .catch(error => {
            if (error.response && [400, 401].includes(error.response.status)) endSession()
            else sessionState.error = '연장하지 못했습니다. 연결 상태를 확인하고 만료 전에 다시 시도해 주세요.'
            throw error
        })
        .finally(() => { pending = null; sessionState.busy = false })
    return pending
}
export function startSessionKeepAlive(options) {
    callback = options.onExpired
    active = options.isActive
    if (started) return
    started = true
    axios.interceptors.request.use(config => {
        if (String(config.url || '').startsWith('/api/')) {
            const token = localStorage.getItem('accessToken')
            if (token) config.headers.token = token
            else delete config.headers.token
        }
        return config
    })
    axios.interceptors.response.use(response => {
        if (sessionState.redirecting && /^\/api\/login\/check/.test(response.config.url || '')) return Promise.reject(new axios.Cancel('Session ended'))
        if (/^\/api\/login(?:\/|$)/.test(response.config.url || '') && response.headers.date) {
            const serverTime = Date.parse(response.headers.date)
            if (Number.isFinite(serverTime)) offset = serverTime - Date.now()
        }
        return response
    }, error => {
        const response = error.response, config = error.config || {}
        // Login failures stay on the login form; stale responses from another token are ignored.
        if (response && response.status === 401 && config.headers &&
            config.headers.token === localStorage.getItem('accessToken')) endSession()
        return Promise.reject(error)
    })
    setInterval(checkSession, 1000)
    window.addEventListener('storage', event => { if (event.key === 'accessToken') checkSession() })
    document.addEventListener('visibilitychange', checkSession)
    window.addEventListener('focus', checkSession)
    window.addEventListener('pageshow', checkSession)
    checkSession()
}
